import React, { useState, useRef, useEffect } from 'react';
import { Camera, Loader2, Trash2, ImageIcon } from 'lucide-react';
import { jobsCol } from './database.js';
import { updateOne } from './lib/db-crud.js';
import { compressImage, uploadFileWithProgress, removeFile } from './lib/storage.js';
import { shortId } from './lib/job-helpers.js';

function PhotoCard({ photo, onRemove, onSaveNote, autoFocusNote }) {
  const noteRef = useRef(null);

  useEffect(() => {
    if (autoFocusNote && noteRef.current) {
      noteRef.current.focus();
    }
  }, [autoFocusNote]);

  return (
    <li className="job-photo-card">
      <a
        href={photo.url}
        target="_blank"
        rel="noopener noreferrer"
        className="job-photo-card-thumb-link"
      >
        <img src={photo.url} alt="" className="job-photo-card-thumb" />
      </a>
      <div className="job-photo-card-body">
        <label className="job-photo-note-label">Note</label>
        <textarea
          ref={noteRef}
          className="job-photo-note"
          rows={2}
          maxLength={300}
          placeholder="What is this? e.g., cracked serpentine belt, driver-side tire wear…"
          defaultValue={photo.note || ''}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v !== (photo.note || '').trim()) onSaveNote(photo.id, v);
          }}
        />
      </div>
      <button
        type="button"
        className="job-photo-card-remove"
        onClick={() => onRemove(photo)}
        aria-label="Remove photo"
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
}

export default function JobPhotosPanel({ job }) {
  const photos = Array.isArray(job?.photos) ? job.photos : [];
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [error, setError] = useState(null);
  const [focusPhotoId, setFocusPhotoId] = useState(null);

  if (!job?.id) return null;

  async function persistPhotos(next) {
    await updateOne(jobsCol(), job.id, { photos: next });
  }

  async function handleAdd(file) {
    if (!file) return;
    setUploading(true);
    setUploadPct(0);
    setError(null);
    setFocusPhotoId(null);
    try {
      const blob = await compressImage(file);
      const photoId = shortId();
      const path = `jobs/${job.id}/photos/${photoId}.jpg`;
      const url = await uploadFileWithProgress(
        path,
        blob,
        { contentType: 'image/jpeg' },
        (p) => setUploadPct(Math.round(p * 100))
      );
      const next = [
        ...photos,
        { id: photoId, url, path, note: '', createdAt: Date.now() },
      ];
      await persistPhotos(next);
      setFocusPhotoId(photoId);
    } catch (e) {
      console.error('Photo upload failed:', e);
      setError(e.message);
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  }

  async function handleSaveNote(photoId, note) {
    setError(null);
    try {
      const next = photos.map((p) =>
        p.id === photoId ? { ...p, note } : p
      );
      await persistPhotos(next);
    } catch (e) {
      console.error('Photo note save failed:', e);
      setError(e.message);
    }
  }

  async function handleRemove(photo) {
    if (!confirm('Remove this photo?')) return;
    setError(null);
    if (focusPhotoId === photo.id) setFocusPhotoId(null);
    try {
      if (photo.path) {
        try { await removeFile(photo.path); } catch (_) { /* may already be gone */ }
      }
      await persistPhotos(photos.filter((p) => p.id !== photo.id));
    } catch (e) {
      console.error('Photo remove failed:', e);
      setError(e.message);
    }
  }

  return (
    <section className="active-job-card job-photos-panel">
      <div className="active-job-card-title-row">
        <h2 className="active-job-card-title">
          <ImageIcon size={16} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          Job photos
        </h2>
        {photos.length > 0 && (
          <span className="job-photos-count muted small">{photos.length} saved</span>
        )}
      </div>
      <p className="muted small" style={{ marginBottom: 12 }}>
        Snap damage, parts, or anything on record. Add a short note to each photo —
        notes copy to the estimate or invoice when you send it.
      </p>

      {error && <p className="form-error">{error}</p>}

      {photos.length > 0 && (
        <ul className="job-photo-list">
          {photos.map((p) => (
            <PhotoCard
              key={p.id}
              photo={p}
              onRemove={handleRemove}
              onSaveNote={handleSaveNote}
              autoFocusNote={focusPhotoId === p.id}
            />
          ))}
        </ul>
      )}

      <label className="job-photo-add">
        <input
          type="file"
          accept="image/*"
          capture="environment"
          disabled={uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) handleAdd(f);
          }}
        />
        {uploading ? (
          <><Loader2 size={14} className="spin" /> Uploading {uploadPct}%…</>
        ) : (
          <><Camera size={14} /> Add photo</>
        )}
      </label>
    </section>
  );
}
